


import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { filesApi } from "services/api/config";
import { getAccessToken } from "services/auth";
import { showNotification } from "services/api/snackbar/index";
import { EmailSendData } from "ui/components/Modal/EmailSendModal/types";
import { InvoiceEmailSendResult, InvoiceInternalNote, InvoiceOutgoing, InvoiceOutgoingEditFormValues, InvoiceOutgoingRow, InvoiceRent, InvoiceRentEditFormValues, InvoiceRentRow, InvoiceSaleSearchOptions, InvoiceRentSearchOptions, InvoiceStatus } from "./types";
import { transformFleetVehicleInvoice, transformFleetVehicleInvoiceOnCreate, transformFleetVehicleInvoiceOnUpdate, transformFleetVehicleInvoiceRow, transformFleetVehicleInvoiceRent, transformFleetVehicleInvoiceRentOnCreate, transformFleetVehicleInvoiceRentOnUpdate, transformFleetVehicleInvoiceRentRow, transformInvoiceStatus } from "./transformations";
import { transformErrorResponse } from "services/api/transformation";
import { Pagination } from "services/search";
import { AvailabilityResult, DataWithPagination, transformAvailabilityResult } from "services/api/types";

/*---------------------------------------------------------------------------------------
   GET - INVOICE STATUSES
-----------------------------------------------------------------------------------------*/
export const getInvoiceStatusesAPI = async (): Promise<InvoiceStatus[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.map(transformInvoiceStatus) ?? [];
};

/*---------------------------------------------------------------------------------------
   SEARCH - VEHICLE - INVOICE - GENERATE INVOICE NUMBER
-----------------------------------------------------------------------------------------*/
export const getInvoiceGenerateNumberAPI = async (
): Promise<string> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/generatenumber`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();




  return response.data?.invoiceNumber;
};

/*---------------------------------------------------------------------------------------
   GET - INVOICE - IS AVAILABLE
-----------------------------------------------------------------------------------------*/
export const getInvoiceIsAvailableAPI = async (): Promise<AvailabilityResult> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/isavailable`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformAvailabilityResult(response.data);
};

/*---------------------------------------------------------------------------------------
   SEARCH - VEHICLE - INFO
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleInvoiceAPI = async (
  id: string
): Promise<InvoiceOutgoing> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoice(response.data);
};

/*---------------------------------------------------------------------------------------
  SEARCH - VEHICLE - INVOICE
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleInvoicesAPI = async (
  pagination: Pagination,
  options?: InvoiceSaleSearchOptions,
): Promise<DataWithPagination<InvoiceOutgoingRow>> => {

  let path = `/v1/fleet/vehicles/invoices/sale?`;

  if (options?.vehicleId) path += `vehicleId=${options.vehicleId}`;
  if (options?.invoiceStatusId) path += `&invoiceStatusId=${options.invoiceStatusId}`;
  if (options?.search) path += `&search=${encodeURIComponent(options.search)}`;
  if (options?.invoiceDateFrom) path += `&invoiceDateFrom=${new Date(options.invoiceDateFrom).toISOString()}`;
  if (options?.invoiceDateTo) path += `&invoiceDateTo=${new Date(options.invoiceDateTo).toISOString()}`;
  if (options?.dueDateFrom) path += `&dueDateFrom=${new Date(options.dueDateFrom).toISOString()}`;
  if (options?.dueDateTo) path += `&dueDateTo=${new Date(options.dueDateTo).toISOString()}`;
  if (options?.totalMin) path += `&totalMin=${options.totalMin}`;
  if (options?.totalMax) path += `&totalMax=${options.totalMax}`;
  if (options?.contactClientId) path += `&contactClientId=${options.contactClientId}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleInvoiceRow
  );
};
/*---------------------------------------------------------------------------------------
  CREATE - VEHICLE - INVOICE
-----------------------------------------------------------------------------------------*/

export const createFleetVehicleInvoiceAPI = async (
  vehicleId: string,
  formValues: InvoiceOutgoingEditFormValues,
  t: (key: string) => string
): Promise<InvoiceOutgoing> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/invoices/sale`,
      method: "POST",
      body: transformFleetVehicleInvoiceOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.invoice.create.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoice(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT - VEHICLE - INVOICE
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleInvoiceAPI = async (
  id: string,
  formValues: InvoiceOutgoingEditFormValues,
  t: (key: string) => string
): Promise<InvoiceOutgoing> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${formValues.vehicleId}/invoices/sale/${id}`,
      method: "PUT",
      body: transformFleetVehicleInvoiceOnUpdate(id, formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.invoice.edit.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoice(response.data);
};



/*---------------------------------------------------------------------------------------
  DELETE - VEHICLE - INVOICE
-----------------------------------------------------------------------------------------*/

export const deleteFleetVehicleInvoiceAPI = async (
  vehicleId: string,
  invoiceId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/invoices/sale/${invoiceId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.invoice.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   PREVIEW - PDF
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleInvoicePdfPreviewAPI = async (
  formValues: InvoiceOutgoingEditFormValues,
): Promise<string> => {
  if (!formValues.id) return "";

  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/${formValues.id}/pdf/preview`,
      method: "GET",
      responseType: "blob",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return URL.createObjectURL(response.data);
};

/*---------------------------------------------------------------------------------------
   PREVIEW - XML (eRačun, UBL 2.1)
-----------------------------------------------------------------------------------------*/
/**
 * UBL XML izlaznog računa. Generira se iz trenutnog stanja računa, pa se dohvaća svaki
 * put iznova — nije spremljena datoteka.
 */
export const getFleetVehicleInvoiceSaleXmlAPI = async (
  invoiceId: string
): Promise<string> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/${invoiceId}/xml`,
      method: "GET",
      responseType: "text",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
   SEND - INVOICE SALE - EMAIL
-----------------------------------------------------------------------------------------*/
/**
 * Šalje račun kupcu e-mailom. PDF računa dodaje backend, ovdje idu samo dodatni privici
 * koje je korisnik odabrao u modalu.
 */
export const sendFleetVehicleInvoiceSaleEmailAPI = async (
  invoiceId: string,
  emailData: EmailSendData
): Promise<InvoiceEmailSendResult> => {
  const formData = new FormData();

  emailData.recipients.forEach((recipient) => formData.append("Recipients", recipient));
  formData.append("ReplyTo", emailData.replyTo);
  formData.append("Subject", emailData.subject);
  formData.append("Content", emailData.content);

  if (emailData.cc) {
    formData.append("CC", emailData.cc);
  }

  for (let index = 0; index < emailData.attachments.length; index++) {
    const file = emailData.attachments[index];
    formData.append(`Attachments[${index}].Id`, file.id);
    formData.append(`Attachments[${index}].Title`, file.title ?? file.name);
    formData.append(`Attachments[${index}].IsNew`, String(file.isNew ?? false));

    if (file.url) {
      formData.append(`Attachments[${index}].Url`, file.url);
      const fileResponse = await fetch(file.url);
      const blob = await fileResponse.blob();
      formData.append(`Attachments[${index}].File`, blob, file.name);
    }
  }

  const config = {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  };

  const response = await filesApi.post(
    `/v1/fleet/vehicles/invoices/sale/${invoiceId}/send-email`,
    formData,
    config
  );

  const result: InvoiceEmailSendResult = {
    sent: response.data?.sent ?? false,
    invoiceStatusId: response.data?.invoiceStatusId ?? null,
    emailSentAt: response.data?.emailSentAt ?? null,
  };

  if (result.sent) {
    showNotification("Račun je poslan kupcu na e-mail", { variant: "success" });
  } else {
    showNotification("Slanje računa na e-mail nije uspjelo", { variant: "error" });
  }

  return result;
};

/*---------------------------------------------------------------------------------------
   POST - FISCALIZE INVOICE
-----------------------------------------------------------------------------------------*/
export const fiscalizeInvoiceAPI = async (
  invoiceId: string,
  t: (key: string) => string
): Promise<{ jir: string; zki: string }> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/${invoiceId}/fiscalize`,
      method: "POST",
    },
    {
      getMessage: {
        success: () => "Račun je uspješno fiskaliziran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data;
};

export const getInvoiceInternalNotesAPI = async (invoiceId: string): Promise<InvoiceInternalNote[]> => {
  const response = await createApiCall({
    path: `/v1/fleet/vehicles/invoices/sale/${invoiceId}/notes`,
    method: "GET",
  })();
  return response.data ?? [];
};

export const createInvoiceInternalNoteAPI = async (
  invoiceId: string,
  content: string,
): Promise<InvoiceInternalNote> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/${invoiceId}/notes`,
      method: "POST",
      body: { content },
    },
    {
      getMessage: {
        success: () => "Napomena je dodana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
  return response.data;
};

export const deleteInvoiceInternalNoteAPI = async (
  invoiceId: string,
  noteId: string,
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/${invoiceId}/notes/${noteId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Napomena je obrisana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   TOGGLE STATUS HISTORY
-----------------------------------------------------------------------------------------*/
export const toggleInvoiceStatusHistoryAPI = async (
  invoiceId: string,
  statusId: string,
  checked: boolean
): Promise<InvoiceOutgoing> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/sale/${invoiceId}/status-history/${statusId}`,
      method: "PUT",
      body: { checked },
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoice(response.data);
};

/*=======================================================================================
   INVOICE RENT
=========================================================================================*/

/*---------------------------------------------------------------------------------------
   GET - VEHICLE - INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleInvoiceRentAPI = async (
  id: string
): Promise<InvoiceRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/rent/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoiceRent(response.data);
};

/*---------------------------------------------------------------------------------------
  SEARCH - VEHICLE - INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleInvoiceRentsAPI = async (
  pagination: Pagination,
  options?: InvoiceRentSearchOptions,
): Promise<DataWithPagination<InvoiceRentRow>> => {

  let path = `/v1/fleet/vehicles/invoices/rent?`;

  if (options?.vehicleId) path += `vehicleId=${options.vehicleId}`;
  if (options?.invoiceStatusId) path += `&invoiceStatusId=${options.invoiceStatusId}`;
  if (options?.search) path += `&search=${encodeURIComponent(options.search)}`;
  if (options?.invoiceDateFrom) path += `&invoiceDateFrom=${new Date(options.invoiceDateFrom).toISOString()}`;
  if (options?.invoiceDateTo) path += `&invoiceDateTo=${new Date(options.invoiceDateTo).toISOString()}`;
  if (options?.dueDateFrom) path += `&dueDateFrom=${new Date(options.dueDateFrom).toISOString()}`;
  if (options?.dueDateTo) path += `&dueDateTo=${new Date(options.dueDateTo).toISOString()}`;
  if (options?.totalMin) path += `&totalMin=${options.totalMin}`;
  if (options?.totalMax) path += `&totalMax=${options.totalMax}`;
  if (options?.contactClientId) path += `&contactClientId=${options.contactClientId}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleInvoiceRentRow
  );
};

/*---------------------------------------------------------------------------------------
  CREATE - VEHICLE - INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleInvoiceRentAPI = async (
  vehicleId: string,
  formValues: InvoiceRentEditFormValues,
  t: (key: string) => string
): Promise<InvoiceRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/invoices/rent`,
      method: "POST",
      body: transformFleetVehicleInvoiceRentOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.invoice.create.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoiceRent(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT - VEHICLE - INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleInvoiceRentAPI = async (
  id: string,
  formValues: InvoiceRentEditFormValues,
  t: (key: string) => string
): Promise<InvoiceRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${formValues.vehicleId}/invoices/rent/${id}`,
      method: "PUT",
      body: transformFleetVehicleInvoiceRentOnUpdate(id, formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.invoice.edit.message.success")}`,
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoiceRent(response.data);
};

/*---------------------------------------------------------------------------------------
  DELETE - VEHICLE - INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleInvoiceRentAPI = async (
  vehicleId: string,
  invoiceId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/invoices/rent/${invoiceId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.invoice.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   PREVIEW - PDF - INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleInvoiceRentPdfPreviewAPI = async (
  formValues: InvoiceRentEditFormValues,
): Promise<string> => {
  if (!formValues.id) return "";

  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/rent/${formValues.id}/pdf/preview`,
      method: "GET",
      responseType: "blob",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return URL.createObjectURL(response.data);
};

/*---------------------------------------------------------------------------------------
   POST - FISCALIZE INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const fiscalizeInvoiceRentAPI = async (
  invoiceId: string,
  t: (key: string) => string
): Promise<{ jir: string; zki: string }> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/rent/${invoiceId}/fiscalize`,
      method: "POST",
    },
    {
      getMessage: {
        success: () => "Račun je uspješno fiskaliziran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
   TOGGLE STATUS HISTORY - INVOICE RENT
-----------------------------------------------------------------------------------------*/
export const toggleInvoiceRentStatusHistoryAPI = async (
  invoiceId: string,
  statusId: string,
  checked: boolean
): Promise<InvoiceRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/rent/${invoiceId}/status-history/${statusId}`,
      method: "PUT",
      body: { checked },
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleInvoiceRent(response.data);
};

export const getInvoiceRentInternalNotesAPI = async (invoiceId: string): Promise<InvoiceInternalNote[]> => {
  const response = await createApiCall({
    path: `/v1/fleet/vehicles/invoices/rent/${invoiceId}/notes`,
    method: "GET",
  })();
  return response.data ?? [];
};

export const createInvoiceRentInternalNoteAPI = async (
  invoiceId: string,
  content: string,
): Promise<InvoiceInternalNote> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/rent/${invoiceId}/notes`,
      method: "POST",
      body: { content },
    },
    {
      getMessage: {
        success: () => "Napomena je dodana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
  return response.data;
};

export const deleteInvoiceRentInternalNoteAPI = async (
  invoiceId: string,
  noteId: string,
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/invoices/rent/${invoiceId}/notes/${noteId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Napomena je obrisana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
  return response.data?.deleted;
};
